import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Alert, CircularProgress } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getUserInviteLink } from '../services/userManagementService';

const InviteLinkPanel = ({ userId, orgId }) => {
  const [linkData, setLinkData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchInviteLink = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getUserInviteLink(orgId, userId);
        setLinkData(data);
      } catch (err) {
        console.error('Failed to load invite link:', err);
        setError(err.message || 'Failed to load invite link');
      } finally {
        setLoading(false);
      }
    };

    if (userId && orgId) {
      fetchInviteLink();
    }
  }, [userId, orgId]);

  const handleCopy = () => {
    const fullUrl = `${window.location.origin}${linkData.relative_url}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);

      // Send GA event for link copy
      if (window.gtag) {
        window.gtag('event', 'invite_link_copied', {
          user_id: userId,
          invite_token: linkData.token
        });
      }
    }).catch(err => {
      console.error('Failed to copy to clipboard:', err);
      setError('Failed to copy to clipboard');
    });
  };

  if (loading) {
    return (
      <Box sx={{ mb: 2, textAlign: 'center' }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!linkData) {
    return null;
  }

  const fullUrl = `${window.location.origin}${linkData.relative_url}`;

  return (
    <Box sx={{ mb: 2, pb: 2, borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }}>
      <Typography
        variant="h6"
        sx={{
          fontFamily: 'Bebas Neue',
          letterSpacing: 1,
          mb: 0.5
        }}
      >
        Your Invite Link
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Share this link to invite users
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant="body2"
          sx={{
            flex: 1,
            p: 1,
            backgroundColor: 'grey.100',
            borderRadius: 1,
            fontSize: '0.75rem',
            wordBreak: 'break-all'
          }}
        >
          {fullUrl}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<ContentCopyIcon />}
          onClick={handleCopy}
          sx={{
            backgroundColor: '#021c2d',
            '&:hover': { backgroundColor: '#042a44' }
          }}
        >
          {copied ? 'Copied!' : 'Copy'}
        </Button>
      </Box>
    </Box>
  );
};

export default InviteLinkPanel;
